import { useQuery } from "@apollo/client/react";
import { Box, Flex, Table, TruncatedText } from "@nulogy/components";
import { useFormikContext } from "formik";
import { GetAllLines } from "hooks/apollo/line/line.gql";
import { GetAllShifts } from "hooks/apollo/shift/shift.gql";
import React from "react";
import styled, { useTheme } from "styled-components";
import AvailabilityCell from "./AvailabilityCell";
import AvailabilityDateHeader from "./AvailabilityDateHeader";
import AvailabilityShiftHeader from "./AvailabilityShiftHeader";
import { calculateTotalTableWidth, columnWidth } from "./WidthCalculator";

const Container = styled.div`
  position: relative;
  max-height: calc(100vh - (460px));
  height: fit-content;
  width: 100%;
  overflow: scroll;
`;

const ScrollTable = styled(Table)(
  ({ shiftCount, theme }) => `
  td,
  th {
    background: white;
    z-index: 0;
    width: ${columnWidth(shiftCount)}px;
  }

  td:nth-child(${shiftCount}n + ${shiftCount + 1}),
  th:nth-child(${shiftCount}n + ${shiftCount + 1}) {
    border-right: 1px solid ${theme.colors.lightGrey};
    padding-right: 0px
  }

  thead tr {
    border: none;
  }

  thead th {
    position: -webkit-sticky;
    position: sticky;
    top: 42px;
    z-index: 2;
    background: white;
  }

  thead th:first-child {
    left: 0;
    top: 42px;
    z-index: 8;
    width: 108px;
  }

  tbody {
    overflow: scroll;
    border-bottom: 1px solid ${theme.colors.lightGrey};
  }

  thead {
    position: sticky;
    box-shadow: 0 1px #ddd;
    top: 42px;
    z-index: 8;
  }

  tr > td:first-child {
    position: -webkit-sticky;
    position: sticky;
    left: 0;
    z-index: 7;
    width: 108px;
  }

  // Hack to make the table not stretch if it is smaller than the viewport
  width: 1px;
  top: -14px;
`,
);

// Held to the width on show: it sits outside the table's scroller and so stays put as the table scrolls,
// and anything wider than the page overflows it.
const TopBorder = styled(Box)({
  borderBottom: "1px solid lightGrey",
  height: "3px",
  left: "0px",
  marginBottom: 0,
  maxWidth: "100%",
  position: "absolute",
  top: "-2px",
  zIndex: "400",
});

const LineBox = styled(Box)({
  background: "white",
  zIndex: 7,
});

const LineHeader = styled.div(({ theme }) => ({
  color: theme.colors.black,
  display: "block",
  margin: 0,
  maxWidth: columnWidth,
  minWidth: columnWidth,
  padding: 0,
}));

const PlaceholderBox = styled(Box)({
  background: "white",
  float: "left",
  height: "56px",
  left: 0,
  maxWidth: "107px",
  minWidth: "107px",
  position: "sticky",
  top: 0,
  zIndex: 10,
});

function renderAvailability({ column, row }) {
  return <AvailabilityCell column={column} row={row} />;
}

function renderLineHeader({ cellData }) {
  return (
    <Flex alignItems="center" className="spec-line-name" height="56px">
      <TruncatedText color="darkGrey" fullWidth tooltipProps={{ placement: "right" }} width="100px">
        {cellData}
      </TruncatedText>
    </Flex>
  );
}

function matchingShift(shiftId, date) {
  return (lineShift) => lineShift.shift === shiftId && lineShift.date === date;
}

function lineShiftEnabled(date) {
  return (lineShift) => !lineShift.disabled && lineShift.date === date;
}

function buildColumns(cols, dates, shifts, headerFormatter) {
  dates.forEach((day) => {
    shifts.forEach((shift) => {
      cols.push({
        cellRenderer: renderAvailability,
        dataKey: `${shift.id}-${day}`,
        headerFormatter,
        label: `${shift.name}-${day}`,
        metadata: {
          date: day,
          shift: shift.id,
          shifts,
        },
        width: `${columnWidth(shifts.length)}px`,
      });
    });
  });
}

function buildRows(rows, lines, cols, availabilities) {
  lines.forEach((line) => {
    const innerLine = { line: line.name };
    cols.forEach((col) => {
      const avIndex = availabilities.findIndex(
        (av) => av.shift === col.metadata.shift && av.line === line.id && av.date === col.metadata.date,
      );
      innerLine[`${col.metadata.shift}-${col.metadata.date}`] = {
        ...availabilities[avIndex],
      };
    });
    rows.push(innerLine);
  });
}

export function AvailabilityOverridesTable({ dates, availabilities }) {
  const { data, loading } = useQuery(GetAllShifts);
  const { data: lineData, loading: loadingLines } = useQuery(GetAllLines, { variables: { activeOnly: true } });
  const { setFieldValue } = useFormikContext();
  const theme = useTheme();

  if (loading || loadingLines) return null;

  const { shifts } = data;

  const { lines } = lineData;

  const setLineShiftChecked = ({ lineShift, checked }) =>
    setFieldValue(`availabilities[${lineShift.index}]['available']`, checked, false);
  const checkLineShift = (lineShift) => setLineShiftChecked({ checked: true, lineShift });
  const uncheckLineShift = (lineShift) => setLineShiftChecked({ checked: false, lineShift });

  const selectAllForShift = (shift, date) => {
    availabilities.filter(matchingShift(shift.id, date)).forEach(checkLineShift);
  };
  const deselectAllForShift = (shift, date) =>
    availabilities.filter(matchingShift(shift.id, date)).forEach(uncheckLineShift);
  const selectAllForDate = (date) => availabilities.filter(lineShiftEnabled(date)).forEach(checkLineShift);
  const deselectAllForDate = (date) => availabilities.filter(lineShiftEnabled(date)).forEach(uncheckLineShift);

  function findShift(shiftId) {
    return shifts.find((candidateShift) => candidateShift.id === shiftId);
  }

  function renderDateHeader(date, index) {
    return (
      <AvailabilityDateHeader
        date={date}
        deselectAllForDate={deselectAllForDate}
        index={index}
        key={index}
        selectAllForDate={selectAllForDate}
        shifts={shifts}
      />
    );
  }

  function lineHeaderFormatter() {
    return (
      <LineBox pt="42px">
        <LineHeader />
      </LineBox>
    );
  }

  function headerFormatter({ metadata }) {
    const { shift: shiftId, date } = metadata;
    const shift = findShift(shiftId);

    return (
      <AvailabilityShiftHeader
        date={date}
        deselectAllForShift={deselectAllForShift}
        selectAllForShift={selectAllForShift}
        shift={shift}
        shiftCount={shifts.length}
      />
    );
  }

  const rows = [];
  const cols = [
    {
      cellRenderer: renderLineHeader,
      dataKey: "line",
      headerFormatter: lineHeaderFormatter,
      label: "Line",
      metadata: {},
    },
  ];

  buildColumns(cols, dates, shifts, headerFormatter);
  buildRows(rows, lines, cols, availabilities);

  return (
    <>
      <TopBorder width={calculateTotalTableWidth(shifts, dates)} />
      <Container borderRight={`1px solid ${theme.colors.lightGrey}`}>
        <PlaceholderBox />
        <div style={{ clear: "both", position: "relative", top: "-56px" }}>
          <Flex style={{ background: "white", position: "sticky", top: "0px", zIndex: 9 }}>
            {dates.map((date, index) => renderDateHeader(date, index))}
          </Flex>
          <ScrollTable
            columns={cols}
            compact
            data-testid="table"
            keyField="line"
            maxHeight={200}
            rowHovers={false}
            rows={rows}
            shiftCount={shifts.length}
          />
        </div>
      </Container>
    </>
  );
}
